import React, { useState } from 'react';
import { useLocation, Link, Routes, Route, Navigate } from 'react-router-dom';
import ApiService from '../../services/api';
import { getToken } from '../../utils/auth';
import { useNotification } from '../../context/NotificationContext';

// Quotes Component
const Quotes = () => {
  const [symbols, setSymbols] = useState<string>('');
  const [type, setType] = useState<'crypto' | 'stock'>('stock');
  const [source, setSource] = useState<string>('alpaca');
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const symbolsArray = symbols.split(',').map((s) => s.trim());
      const result = await api.getQuotes(symbolsArray, type, source);
      setQuotes(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      showNotification('Failed to fetch quotes. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Quotes</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbols (comma separated)
            </label>
            <input
              type="text"
              value={symbols}
              onChange={(e) => setSymbols(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL,MSFT,GOOGL"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'crypto' | 'stock')}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="stock">Stock</option>
              <option value="crypto">Crypto</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="alpaca">Alpaca</option>
              <option value="finnhub">Finnhub</option>
              <option value="tradier">Tradier</option>
              <option value="coinbase">Coinbase</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Quotes'}
            </button>
          </div>
        </div>
      </form>

      {quotes.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Symbol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Trade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quote
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Daily Change
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Volume
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quotes.map((quote, index) => {
                const latestDaily = quote.daily?.[quote.daily.length - 1];
                const previousDaily = quote.daily?.[quote.daily.length - 2];
                const priceChange =
                  latestDaily && previousDaily
                    ? latestDaily.close - previousDaily.close
                    : null;
                const priceChangePercent =
                  latestDaily && previousDaily && previousDaily.close
                    ? (priceChange / previousDaily.close) * 100
                    : null;

                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {quote.symbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      ${quote.lastTrade?.price?.toFixed(2) || 'N/A'} (
                      {quote.lastTrade?.size || 0})
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        Bid: ${quote.quote?.bid?.toFixed(2) || 'N/A'} (
                        {quote.quote?.bidSize || 0})
                      </div>
                      <div>
                        Ask: ${quote.quote?.ask?.toFixed(2) || 'N/A'} (
                        {quote.quote?.askSize || 0})
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${priceChange !== null ? (priceChange > 0 ? 'text-green-600' : priceChange < 0 ? 'text-red-600' : '') : ''}`}
                    >
                      {priceChange !== null && priceChangePercent !== null
                        ? `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)} (${priceChangePercent.toFixed(2)}%)`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {latestDaily?.volume?.toLocaleString() || 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Snapshots Component
const Snapshots = () => {
  const [symbols, setSymbols] = useState<string>('');
  const [type, setType] = useState<'crypto' | 'stock'>('stock');
  const [source, setSource] = useState<string>('alpaca');
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const symbolsArray = symbols.split(',').map((s) => s.trim());
      const result = await api.getSnapshots(symbolsArray, type, source);
      setSnapshots(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching snapshots:', error);
      showNotification('Failed to fetch snapshots. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Snapshots</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbols (comma separated)
            </label>
            <input
              type="text"
              value={symbols}
              onChange={(e) => setSymbols(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL,MSFT,GOOGL"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'crypto' | 'stock')}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="stock">Stock</option>
              <option value="crypto">Crypto</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="alpaca">Alpaca</option>
              <option value="finnhub">Finnhub</option>
              <option value="tradier">Tradier</option>
              <option value="coinbase">Coinbase</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Snapshots'}
            </button>
          </div>
        </div>
      </form>

      {snapshots.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Symbol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Trade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quote
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Daily Change
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Volume
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {snapshots.map((snapshot, index) => {
                const latestDaily = snapshot.daily?.[snapshot.daily.length - 1];
                const previousDaily =
                  snapshot.daily?.[snapshot.daily.length - 2];
                const priceChange =
                  latestDaily && previousDaily
                    ? latestDaily.close - previousDaily.close
                    : null;
                const priceChangePercent =
                  latestDaily && previousDaily && previousDaily.close
                    ? (priceChange / previousDaily.close) * 100
                    : null;

                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {snapshot.symbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      ${snapshot.lastTrade?.price?.toFixed(2) || 'N/A'} (
                      {snapshot.lastTrade?.size || 0})
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        Bid: ${snapshot.quote?.bid?.toFixed(2) || 'N/A'} (
                        {snapshot.quote?.bidSize || 0})
                      </div>
                      <div>
                        Ask: ${snapshot.quote?.ask?.toFixed(2) || 'N/A'} (
                        {snapshot.quote?.askSize || 0})
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${priceChange !== null ? (priceChange > 0 ? 'text-green-600' : priceChange < 0 ? 'text-red-600' : '') : ''}`}
                    >
                      {priceChange !== null && priceChangePercent !== null
                        ? `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)} (${priceChangePercent.toFixed(2)}%)`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {latestDaily?.volume?.toLocaleString() || 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Historical Bars Component
const HistoricalBars = () => {
  const [symbols, setSymbols] = useState<string>('');
  const [preset, setPreset] = useState<string>('1D');
  const [type, setType] = useState<'crypto' | 'stock'>('stock');
  const [source, setSource] = useState<string>('alpaca');
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const symbolsArray = symbols.split(',').map((s) => s.trim());

      // Get historical bars data
      const result = await api.getHistoricalBars(
        symbolsArray,
        preset as any,
        type,
        source,
      );
      setHistoricalData(Array.isArray(result) ? result : []);

      // Get chart for the first symbol
      if (symbolsArray.length > 0) {
        try {
          const chartResult = await api.getHistoricalBarsChart(
            [symbolsArray[0]],
            preset as any,
            type,
            source,
          );
          setChartData(
            chartResult &&
              typeof chartResult === 'object' &&
              'imageData' in chartResult
              ? typeof chartResult.imageData === 'string'
                ? chartResult.imageData
                : null
              : null,
          );
        } catch (chartError) {
          console.error('Error fetching chart:', chartError);
          showNotification(
            'Failed to fetch chart. Data is still available in table format.',
            'warning',
          );
        }
      }
    } catch (error) {
      console.error('Error fetching historical bars:', error);
      showNotification(
        'Failed to fetch historical bars. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Historical Bars</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbols (comma separated)
            </label>
            <input
              type="text"
              value={symbols}
              onChange={(e) => setSymbols(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL,MSFT,GOOGL"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preset
            </label>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="1D">1 Day</option>
              <option value="5D">5 Days</option>
              <option value="30D">30 Days</option>
              <option value="6M">6 Months</option>
              <option value="1Y">1 Year</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'crypto' | 'stock')}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="stock">Stock</option>
              <option value="crypto">Crypto</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="alpaca">Alpaca</option>
              <option value="finnhub">Finnhub</option>
              <option value="tradier">Tradier</option>
              <option value="coinbase">Coinbase</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Historical Data'}
            </button>
          </div>
        </div>
      </form>

      {chartData && (
        <div className="mb-6 bg-white p-4 rounded shadow">
          <h3 className="text-lg font-medium mb-2">
            Chart for {historicalData[0]?.symbol}
          </h3>
          <img
            src={`data:image/png;base64,${chartData}`}
            alt={`${historicalData[0]?.symbol} chart`}
            className="max-w-full h-auto"
          />
        </div>
      )}

      {historicalData.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <h3 className="text-lg font-medium mb-2">Historical Data</h3>

          {historicalData.map((data, index) => (
            <div key={index} className="mb-6">
              <h4 className="text-md font-medium mb-2">{data.symbol}</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Open
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      High
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Low
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Close
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Volume
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.bars.slice(0, 20).map((bar: any, barIndex: number) => (
                    <tr key={barIndex}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(bar.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ${bar.open?.toFixed(2) || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ${bar.high?.toFixed(2) || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ${bar.low?.toFixed(2) || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ${bar.close?.toFixed(2) || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {bar.volume?.toLocaleString() || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.bars.length > 20 && (
                <div className="mt-2 text-sm text-gray-500">
                  Showing 20 of {data.bars.length} bars
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Market Data Component
const MarketData = () => {
  const location = useLocation();

  return (
    <div>
      <div className="mb-6">
        <nav className="flex space-x-4">
          <Link
            to="/intel/market/quotes"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/market/quotes'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Quotes
          </Link>
          <Link
            to="/intel/market/snapshots"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/market/snapshots'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Snapshots
          </Link>
          <Link
            to="/intel/market/historical"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/market/historical'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Historical Bars
          </Link>
        </nav>
      </div>

      <Routes>
        <Route path="quotes" element={<Quotes />} />
        <Route path="snapshots" element={<Snapshots />} />
        <Route path="historical" element={<HistoricalBars />} />
        <Route
          path="*"
          element={<Navigate to="/intel/market/quotes" replace />}
        />
      </Routes>
    </div>
  );
};

export default MarketData;
